import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { logSwallow } from "../lib/log";

/**
 * useOpportunityCards — polls /api/opportunity/nearby on a low cadence when
 * OPPORTUNITY mode is on.
 *
 * Design notes
 * ------------
 * - Backend already caches results 12h at the (business, city) level, so the
 *   only reason to re-fetch client-side is when: (a) the user's presence
 *   changed to a new tile, or (b) they just toggled mode ON.
 * - We fetch on mount + when `active` flips true. We also refresh every 5 min
 *   as a safety net if the user's been sitting in the same place — the
 *   backend cache will short-circuit so this is cheap.
 * - When `active` flips false, we clear the cards immediately (fade-out is
 *   driven by <AnimatePresence> in the parent).
 */
export function useOpportunityCards({ active }) {
  const [cards, setCards] = useState([]);
  const activeRef = useRef(active);
  activeRef.current = active;

  const load = useCallback(async () => {
    if (!activeRef.current) { setCards([]); return; }
    try {
      const r = await api.get("/opportunity/nearby");
      // Guard: if the user toggled OFF between request-send and response,
      // don't paint stale cards.
      if (!activeRef.current) return;
      setCards(r.data?.opportunities || []);
    } catch (e) { logSwallow("opportunity.fetch", e); }
  }, []);

  useEffect(() => {
    if (!active) { setCards([]); return undefined; }
    load();
    const t = setInterval(load, 5 * 60 * 1000); // 5-min safety refresh
    return () => clearInterval(t);
  }, [active, load]);

  return { cards, refresh: load };
}
